/**
 * lib/errorReporter.js
 * ===================
 * Cliente global que envia erros do frontend web para o Error Monitor
 * (POST /api/errors/report). Dedup local + throttle simples para não
 * inundar o backend em caso de loop infinito.
 *
 * Uso automático:
 *   import { initErrorReporter } from '@/lib/errorReporter';
 *   initErrorReporter();  // chamado em index.js UMA vez ao arranque
 *
 * Uso manual (para logar events importantes que não são exceptions):
 *   import { reportError } from '@/lib/errorReporter';
 *   reportError({ level: 'warn', category: 'upload_rejected', message: '...' });
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dedup local: mesma fingerprint no mesmo minuto vai apenas 1 vez ao servidor.
const _sent = new Map();  // fingerprint → timestamp
const DEDUP_MS = 60 * 1000;
let _throttleCount = 0;
const MAX_REPORTS_PER_MINUTE = 60;
setInterval(() => { _throttleCount = 0; }, 60 * 1000);

function _fingerprint(payload) {
  const s = `${payload.source}|${payload.category || ''}|${(payload.message || '').slice(0, 200)}`;
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  return String(h);
}

export async function reportError(input) {
  if (_throttleCount >= MAX_REPORTS_PER_MINUTE) return;
  _throttleCount++;
  const payload = {
    source: 'web',
    level: 'error',
    ...input,
    app_version: process.env.REACT_APP_VERSION || 'web',
    url: input.url || (typeof window !== 'undefined' ? window.location.pathname : ''),
  };
  const fp = _fingerprint(payload);
  const now = Date.now();
  const last = _sent.get(fp);
  if (last && (now - last) < DEDUP_MS) return;
  _sent.set(fp, now);
  try {
    // Não usa axios para evitar loop se axios é o próprio autor do erro
    await fetch(`${API}/errors/report`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (_) {
    // Silencioso — não conseguir reportar não deve estoirar a app
  }
}

export function initErrorReporter() {
  if (typeof window === 'undefined') return;
  if (window.__tectur_error_reporter_installed) return;
  window.__tectur_error_reporter_installed = true;

  // 1. JS crashes globais
  window.addEventListener('error', (evt) => {
    const msg = evt?.message || 'Uncaught error';
    // Ignora erros de scripts externos (CORS "Script error")
    if (msg === 'Script error.' && !evt.filename) return;
    reportError({
      category: 'js_error',
      message: msg,
      stack: evt?.error?.stack || `at ${evt?.filename || '?'}:${evt?.lineno || '?'}:${evt?.colno || '?'}`,
    });
  });

  // 2. Promise rejections não capturadas
  window.addEventListener('unhandledrejection', (evt) => {
    const reason = evt?.reason;
    let message, stack;
    if (reason instanceof Error) {
      message = `Unhandled: ${reason.name}: ${reason.message}`;
      stack = reason.stack;
    } else {
      message = `Unhandled promise: ${String(reason).slice(0, 300)}`;
      stack = null;
    }
    reportError({ category: 'unhandled_promise', message, stack });
  });

  // 3. Axios interceptor — apanha 4xx/5xx e erros de rede
  try {
    axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const status = err?.response?.status;
        const url = err?.config?.url || '';
        // Só reporta 500+ e erros de rede (sem response). 4xx são intencionais.
        if (!err.response || (status && status >= 500)) {
          reportError({
            level: 'error',
            category: !err.response ? 'network' : 'api_500',
            message: err?.response?.data?.detail
                     || err?.message
                     || 'Network error',
            url: url.replace(API, ''),
            http_method: err?.config?.method?.toUpperCase(),
            http_status: status,
            stack: err?.stack,
          });
        }
        return Promise.reject(err);
      }
    );
  } catch (_) {
    // Axios não disponível — ignora silenciosamente
  }
}
